import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import 'react-select-v1/dist/react-select.css';
import { Entity, Site } from '@debbie/common/dist/index.js';
import { BlameLogRefType, BlameLogType, } from '@debbie/common/dist/blamelog/index.js';
import { InteractionType, Direction, OutgoingState, IncomingState, } from '@debbie/common/dist/interactions/index.js';
import { blameLogs } from '@debbie/cortex/dist/api/index.js';
import * as interactionsAPI from '@debbie/cortex/dist/api/interactions.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
import THEME from '../../theme.js';
import Clock from '../Icons/Clock.js';
import EyeOff from '../Icons/EyeOff.js';
import InteractionSummary from '../InteractionSummary/index.js';
import HistItem from './HistItem.js';
import { CornerUpLeft } from '../Icons/index.js';
import { styled } from 'styled-components';
import q from '@debbie/cortex/dist/query.js';
import { interactionsByReferenceKey } from '@debbie/cortex/dist/interactions/index.js';
import PerformedBy from '../performed-by/PerformedBy.js';
import Modal from '../Modal.js';
import Button from '../Button.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { notification } from '@debbie/cortex/dist/notifications.js';
import SearchInput from '../SearchInput.js';
import { searchAll } from '../../search/index.js';
import { c } from '@debbie/cortex/dist/index.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { selectRole } from '@debbie/cortex/dist/me/selectors.js';
const InteractionItem = (props) => {
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const role = useSelector(selectRole);
    const tenant = useSelector(selectTenant);
    const { historyItem: { data }, referenceId, } = props;
    const { direction, transacted, templateId } = data;
    const isOutgoing = data.direction === Direction.OUTGOING;
    const isIncoming = data.direction === Direction.INCOMING;
    const isPending = data.state === OutgoingState.PENDING;
    const isSent = data.state === OutgoingState.SENT;
    const isMapped = data.state === IncomingState.MAPPED;
    const isPhone = data.type === InteractionType.Phone;
    const isClientNote = data.type === InteractionType.ClientNote;
    const isNote = data.type === InteractionType.Note;
    const isEmail = data.type === InteractionType.Email;
    const isLetter = data.type === InteractionType.Letter;
    const visibleToClients = ['client', 'debtor'].includes(data.minVisibilityRole);
    const options = [];
    const [showThread, setShowThread] = useState(false);
    const [replying, setReplying] = useState(false);
    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    if ((isPending && isOutgoing) ||
        (isClientNote && isOutgoing) ||
        isPhone ||
        isNote ||
        (role && isAdmin(role))) {
        options.push({
            label: lang.interactions.delete,
            onClick: async () => {
                if (isIncoming) {
                    await interactionsAPI.del(data.id);
                }
                else {
                    await interactionsAPI.del(data.id);
                }
                if (tenant) {
                    const blameLog = {
                        details: data,
                        refId: props.referenceId,
                        refType: BlameLogRefType.Case,
                        type: BlameLogType.InteractionDelete,
                    };
                    await blameLogs.createBlameLog(blameLog, tenant.id);
                }
                if (referenceId) {
                    q.invalidate([
                        interactionsByReferenceKey(props.referenceType, referenceId),
                    ]);
                }
            },
        });
    }
    if (templateId) {
        options.push({
            label: lang.interactions.editTemplate,
            onClick: async () => {
                window.open(`/settings/template-editor/${templateId}`, '_blank');
            },
        });
    }
    if (isIncoming && isMapped) {
        options.push({
            label: lang.interactions.unmap,
            onClick: async () => {
                await interactionsAPI.patch(data.id, {
                    referenceId: null,
                    referenceType: null,
                    state: IncomingState.UNMAPPED,
                });
                if (referenceId) {
                    q.invalidate([
                        interactionsByReferenceKey(props.referenceType, referenceId),
                    ]);
                }
            },
        });
    }
    if (!isClientNote && site === Site.Caseworker) {
        options.push({
            label: visibleToClients
                ? lang.interactions.hideFromClient
                : lang.interactions.showToClient,
            onClick: async () => {
                referenceId &&
                    (await interactionsAPI.patch(data.id, {
                        minVisibilityRole: visibleToClients ? 'caseworker' : 'client',
                    }));
                if (referenceId) {
                    q.invalidate([
                        interactionsByReferenceKey(props.referenceType, referenceId),
                    ]);
                }
                if (tenant) {
                    const blameLog = {
                        details: data,
                        refId: props.referenceId,
                        refType: BlameLogRefType.Case,
                        type: BlameLogType.InteractionHideFromClient,
                    };
                    await blameLogs.createBlameLog(blameLog, tenant.id);
                }
            },
        });
    }
    if (site === Site.Caseworker) {
        options.push({
            label: lang.interactions.clone,
            onClick: () => {
                setCloneModalOpen(true);
            },
        });
    }
    const emailPrintFrameRef = useRef(null);
    if (isEmail) {
        options.push({
            label: `${lang.interactions.printPDF}`,
            onClick: async () => {
                emailPrintFrameRef.current = document.createElement('iframe');
                document.body.appendChild(emailPrintFrameRef.current);
                const contentWindow = emailPrintFrameRef.current.contentWindow;
                const doc = contentWindow.document;
                emailPrintFrameRef.current.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-modals');
                emailPrintFrameRef.current.setAttribute('src', 'about:blank');
                const email = data.details;
                doc.open();
                doc.write(`<div>
          <style>body { overflow: hidden; } img { max-height: 200px} @media print {
            @page {
                margin-top: 0;
                margin-bottom: 0;
            }
            body {
                padding-top: 72px;
                padding-bottom: 72px ;
            }
          }</style>
            <span><b>${lang.interactions.from}</b>: ${email.from}</span>
            <hr />
            <span><b>${lang.interactions.sent}</b>: ${moment(data.transacted || data.createdAt).format(THEME.DATE_FORMAT_LONG)}</span>
            <hr />
            <span><b>${lang.interactions.to}</b>: ${email.to}</span>
            <hr />
            <span><b>${lang.interactions.subject}</b>: ${email.subject}</span>
            <hr />
            ${email.html || email.body}
            </div>`);
                doc.close();
                const height = doc?.body?.scrollHeight;
                if (height && emailPrintFrameRef && emailPrintFrameRef.current) {
                    emailPrintFrameRef.current.style.height = `${height}px`;
                }
                emailPrintFrameRef.current.contentWindow?.print();
            },
        });
        options.push({
            label: lang.interactions.thread,
            onClick: () => {
                setShowThread(true);
            },
        });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(HistItem, { date: props.historyItem.date, timespan: props.historyItem.timespan, options: options, heading: React.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingRight: '0.2em',
                    /*alignItems: 'center',*/
                } },
                site === Site.Debtor ? (React.createElement("span", null,
                    lang.interactions[direction === Direction.OUTGOING //Needs to be flipped on debtorweb
                        ? Direction.INCOMING
                        : Direction.OUTGOING],
                    ' ',
                    lang.interactions.debtorInteraction)) : (React.createElement("div", null,
                    React.createElement("span", null,
                        data.createdBy ? (React.createElement(React.Fragment, null,
                            React.createElement(PerformedBy, { performedBy: data.createdBy }),
                            ' ')) : null,
                        !isNote && lang.interactions[direction],
                        ' ',
                        lang.interactionTypes[data.type],
                        ' ',
                        (isEmail || isLetter) && isOutgoing ? (isSent ? (React.createElement("span", { style: {
                                fontSize: '.8em',
                            } },
                            lang.interactions.SENT,
                            isEmail &&
                                transacted &&
                                ` ${lang.interactions.at} ${moment(transacted).format(THEME.DATE_FORMAT_LONG)}`)) : (isPending && React.createElement(Clock, null))) : (''),
                        isIncoming && isEmail && transacted ? (React.createElement("span", { style: {
                                fontSize: '.8em',
                            } },
                            ' ',
                            lang.interactions.journalizedAt,
                            ' ',
                            moment(transacted).format(THEME.DATE_FORMAT_LONG))) : null,
                        site === Site.Caseworker && !visibleToClients && (React.createElement(EyeOff, { style: { marginLeft: 5 }, "data-tooltip-content": lang.interactions.hiddenFromClient, "data-tooltip-id": "tooltip-default" }))))),
                isEmail && (React.createElement(Reply, { "data-tooltip-id": "tooltip-default", "data-tooltip-content": lang.interactions.reply, onClick: () => {
                        setReplying(true);
                    } }))) },
            React.createElement(InteractionSummary, { interaction: data, longCaseId: props.referenceType === Entity.Case ? props.referenceId : undefined, showThread: showThread, setShowThread: setShowThread, replying: replying, setReplying: setReplying })),
        cloneModalOpen && (React.createElement(Modal, { maxWidth: "500px", onToggle: () => setCloneModalOpen(false), header: lang.interactions.clone },
            React.createElement(CloneModal, { interactionId: data.id, closeModal: () => setCloneModalOpen(false) })))));
};
export const CloneModal = (props) => {
    const lang = useSelector((s) => s.lang);
    const [toCase, setToCase] = useState(null);
    const mut = useMutation(() => {
        if (toCase == null) {
            throw new Error('No case selected');
        }
        return interactionsAPI.clone(props.interactionId, toCase.objectID);
    }, {
        onError: (e) => {
            notification({
                title: `${lang.misc.error} (${lang.interactions.clone})`,
                subtitle: e instanceof Error ? e.message : lang.misc.errorOccured,
                theme: 'error',
            });
        },
        onSuccess: (result) => {
            console.log(result);
            props.closeModal();
            notification({
                title: lang.interactions.cloned,
                button: {
                    label: lang.misc.goToCase,
                    callback: () => {
                        if (toCase) {
                            c.redirect.redirect(`/cases/${toCase.caseSeqId}`);
                        }
                    },
                },
            });
        },
    });
    return (React.createElement(CloneWrapper, null,
        React.createElement(SearchInput, { searchDelay: 300, onSelect: setToCase, placeholder: lang.misc.search, searchAll: (q) => searchAll(lang, q), onRemoveStatic: () => setToCase(null), static: toCase ? `${toCase.name} - #${toCase.caseSeqId}` : undefined }),
        React.createElement(Button, { loading: mut.isLoading, disabled: toCase === null, onClick: () => {
                mut.mutate();
            } }, lang.misc.submit)));
};
const CloneWrapper = styled.div `
  display: flex;
  flex-direction: column;
`;
const Reply = styled(CornerUpLeft) `
  border-radius: 10000px;
  cursor: pointer;
  /*border: 1px solid black;
  padding: 0.1em;*/
`;
export default InteractionItem;
//# sourceMappingURL=InteractionItem.js.map